import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { tenantKeys } from "@/lib/queryKeys";
import {
  ONBOARDING_AUTO_STEP_KEYS,
  clearOnboardingProgressFailure,
  stampOnboardingProgressStep,
  type OnboardingAutoStepKey,
  type TenantOnboardingOverview,
} from "@/services/platformTenantsService";

interface Props {
  tenantId: string;
  overview: TenantOnboardingOverview;
}

const STEP_LABEL: Record<OnboardingAutoStepKey, string> = {
  portal_user: "Usuario e-Factura",
  company_info: "Datos de la empresa",
  cae_download: "Descarga de CAEs",
  proveedor_software: "Configurar Fiscalo como tu proveedor de e-Factura",
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function formatStamp(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("es-UY", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/**
 * Root-only view of the tenant's DGI onboarding auto-flow progress.
 * Surfaces:
 *   · Steps stamped done (with timestamp).
 *   · Steps with `_failed` markers — either a real automation failure or
 *     the customer-triggered `manual_intervention` request from the
 *     dashboard's Configurar button. Both get inline actions:
 *       · "Marcar como hecho" stamps the step done + drops the marker.
 *       · "Limpiar marca" drops the marker so the automation can retry.
 */
export function TenantOnboardingAutoFlowCard({
  tenantId,
  overview,
}: Props): JSX.Element | null {
  const queryClient = useQueryClient();
  const progress = overview.dgiAutoOnboardingProgress;
  const failed = isRecord(progress?._failed)
    ? (progress!._failed as Record<string, unknown>)
    : {};
  const failedCount = ONBOARDING_AUTO_STEP_KEYS.filter((k) =>
    Boolean(failed[k]),
  ).length;
  const doneCount = progress
    ? ONBOARDING_AUTO_STEP_KEYS.filter((k) => Boolean(progress[k])).length
    : 0;

  // Hide entirely when there's nothing meaningful — the tenant either
  // didn't start the auto-flow (no progress field) or is operating purely
  // manually with no failure markers worth surfacing here.
  if (!progress || (doneCount === 0 && failedCount === 0)) {
    return null;
  }

  const stampMutation = useMutation({
    mutationFn: (step: OnboardingAutoStepKey) =>
      stampOnboardingProgressStep(tenantId, step),
    onSuccess: () => {
      toast.success("Paso marcado como hecho.");
      void queryClient.invalidateQueries({
        queryKey: tenantKeys.overview(tenantId),
      });
    },
    onError: () => {
      toast.error("No pudimos marcar el paso. Probá de nuevo.");
    },
  });

  const clearMutation = useMutation({
    mutationFn: (step: OnboardingAutoStepKey) =>
      clearOnboardingProgressFailure(tenantId, step),
    onSuccess: () => {
      toast.success("Marca de error limpiada.");
      void queryClient.invalidateQueries({
        queryKey: tenantKeys.overview(tenantId),
      });
    },
    onError: () => {
      toast.error("No pudimos limpiar la marca. Probá de nuevo.");
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4" />
          Onboarding auto-flow
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {failedCount > 0
            ? `${failedCount} paso${failedCount === 1 ? "" : "s"} con intervención manual pendiente.`
            : `${doneCount} / ${ONBOARDING_AUTO_STEP_KEYS.length} pasos completados.`}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {ONBOARDING_AUTO_STEP_KEYS.map((stepKey) => {
          const doneAt = formatStamp(progress[stepKey]);
          const failureMarker =
            typeof failed[stepKey] === "string"
              ? (failed[stepKey] as string)
              : null;
          const isManualIntervention =
            failureMarker === "manual_intervention";
          const isAutomationFailure = Boolean(failureMarker) && !isManualIntervention;
          const isPending = !doneAt && !failureMarker;
          const pendingMutation =
            stampMutation.isPending || clearMutation.isPending;

          return (
            <div
              key={stepKey}
              className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-3">
                {doneAt ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                ) : isManualIntervention ? (
                  <Clock className="mt-0.5 h-4 w-4 text-amber-600" />
                ) : isAutomationFailure ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
                ) : (
                  <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
                )}
                <div className="text-sm">
                  <div className="font-medium">{STEP_LABEL[stepKey]}</div>
                  {doneAt ? (
                    <div className="text-xs text-muted-foreground">
                      Hecho · {doneAt}
                    </div>
                  ) : isManualIntervention ? (
                    <div className="text-xs text-amber-700">
                      El usuario solicitó que Fiscalo lo registre manualmente.
                      Hacé el alta en e-Factura y marcalo como hecho.
                    </div>
                  ) : isAutomationFailure ? (
                    <div className="text-xs text-destructive break-words">
                      Automatización falló: {failureMarker}
                    </div>
                  ) : isPending ? (
                    <div className="text-xs text-muted-foreground">
                      Pendiente — el auto-flow lo ejecuta cuando llega.
                    </div>
                  ) : null}
                </div>
              </div>

              {!doneAt && (
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={pendingMutation}
                    onClick={() => {
                      if (!stampMutation.isPending) {
                        stampMutation.mutate(stepKey);
                      }
                    }}
                  >
                    {stampMutation.isPending &&
                    stampMutation.variables === stepKey ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Marcar como hecho
                  </Button>
                  {failureMarker ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={pendingMutation}
                      onClick={() => {
                        if (!clearMutation.isPending) {
                          clearMutation.mutate(stepKey);
                        }
                      }}
                    >
                      {clearMutation.isPending &&
                      clearMutation.variables === stepKey ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Limpiar marca
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
